// Client-side URL and domain validation
import i18n from '../i18n';

export const URL_LIMITS = {
  MAX_LENGTH: 2048,
} as const;

export interface UrlValidation {
  valid: boolean;
  error: string | null;
}

// Reserved/private IP ranges that should never be sent to backend (SSRF protection)
const RESERVED_HOSTNAMES = [
  'localhost',
  'localhost.localdomain',
  'ip6-localhost',
  'ip6-loopback',
];

/** Check if hostname is a reserved/private IP address */
function isReservedHost(hostname: string): boolean {
  const lower = hostname.toLowerCase();

  // Known reserved hostnames
  if (RESERVED_HOSTNAMES.includes(lower)) return true;

  // IPv6 loopback [::1] — URL parser strips brackets
  if (lower === '::1' || lower === '[::1]') return true;

  // IPv4 checks
  const ipv4Match = lower.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4Match) {
    const [, a, b, c] = ipv4Match.map(Number);
    // 127.0.0.0/8 — loopback
    if (a === 127) return true;
    // 10.0.0.0/8 — private
    if (a === 10) return true;
    // 172.16.0.0/12 — private
    if (a === 172 && b >= 16 && b <= 31) return true;
    // 192.168.0.0/16 — private
    if (a === 192 && b === 168) return true;
    // 169.254.0.0/16 — link-local
    if (a === 169 && b === 254) return true;
    // 0.0.0.0/8 — unspecified
    if (a === 0) return true;
    // 100.64.0.0/10 — CGN shared
    if (a === 100 && b >= 64 && b <= 127) return true;
    // 198.18.0.0/15 — benchmarking
    if (a === 198 && (b === 18 || b === 19)) return true;
    // 224.0.0.0+ — multicast & reserved
    if (a >= 224) return true;
  }

  // IPv6 private ranges (commonly seen in URLs)
  const ipv6 = lower.replace(/^\[|\]$/g, '');
  if (ipv6.startsWith('fe80:')) return true;    // link-local
  if (ipv6.startsWith('fc') || ipv6.startsWith('fd')) return true; // unique local
  if (ipv6 === '::' || ipv6 === '::1') return true;

  // Numeric localhost tricks: 0x7f000001, 2130706433, 017700000001
  if (/^0x[0-9a-f]+$/i.test(lower) || /^\d{8,}$/.test(lower) || /^0\d+$/.test(lower)) return true;

  return false;
}

/** Validate a URL (must be http:// or https:// with valid structure, or www.) */
export function validateUrl(url: string): UrlValidation {
  let trimmed = url.trim();
  if (!trimmed) {
    return { valid: false, error: null };
  }

  // Max length
  if (trimmed.length > URL_LIMITS.MAX_LENGTH) {
    return { valid: false, error: i18n.t('validation.urlTooLong', { max: URL_LIMITS.MAX_LENGTH }) };
  }

  // Auto-accept www. prefix — treat as https://
  if (/^www\./i.test(trimmed)) {
    trimmed = `https://${trimmed}`;
  }

  // Must start with http:// or https://
  if (!/^https?:\/\//i.test(trimmed)) {
    return { valid: false, error: i18n.t('validation.urlMustStartWith') };
  }

  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname;

    if (!host) {
      return { valid: false, error: i18n.t('validation.urlMissingHostname') };
    }

    // Block reserved/private IP addresses (SSRF protection)
    if (isReservedHost(host)) {
      return { valid: false, error: i18n.t('validation.urlPrivateAddress') };
    }

    // Strip trailing dot (browser normalizes "test." to "test.")
    const cleanHost = host.endsWith('.') ? host.slice(0, -1) : host;

    // Must have at least one dot separating parts (e.g. example.com)
    if (!cleanHost.includes('.')) {
      return { valid: false, error: i18n.t('validation.urlMissingDomain') };
    }

    // TLD must be at least 2 letters (e.g. .com, .ro, .org)
    const parts = cleanHost.split('.');
    const tld = parts[parts.length - 1];
    if (!tld || tld.length < 2 || !/^[a-zA-Z]{2,}$/.test(tld)) {
      return { valid: false, error: i18n.t('validation.urlInvalidTld') };
    }

    // Each part must be non-empty and valid
    for (const part of parts) {
      if (!part || /\s/.test(part)) {
        return { valid: false, error: i18n.t('validation.urlInvalidSegments') };
      }
    }

    return { valid: true, error: null };
  } catch {
    return { valid: false, error: i18n.t('validation.urlInvalidFormat') };
  }
}

/** Validate a domain name or URL (for DomainAnalysis) */
export function validateDomainInput(input: string): UrlValidation {
  const trimmed = input.trim();
  if (!trimmed) {
    return { valid: false, error: null };
  }

  // If it looks like a URL (http://, https://, or www.), validate as URL
  if (/^(https?:\/\/|www\.)/i.test(trimmed)) {
    return validateUrl(trimmed);
  }

  // Block reserved hostnames even as bare domain
  if (isReservedHost(trimmed)) {
    return { valid: false, error: i18n.t('validation.domainPrivateAddress') };
  }

  // Otherwise validate as domain name
  // Domain: letters, numbers, hyphens, dots — must have at least one dot
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  if (!domainRegex.test(trimmed)) {
    return { valid: false, error: i18n.t('validation.domainInvalidFormat') };
  }

  return { valid: true, error: null };
}
